// Annotations on a document version. The backend keys them on the version a
// file is; the client only ever holds a fileId, so that is what these take.
// The editor speaks `ItemsByPage`, the wire a flat list of items that name their
// page — `toWireItems` / `groupByPage` are the only translation between them.

import { authedFetch } from '@apis/auth';
import type { ItemsByPage, MarkupItem } from '@components/Pages/AnnotationsPage/utils/markupTypes';

/** What every stored item adds to the editor's own shape. */
interface IItemIdentity {
  id: string;
  page: number;
}

/** A stored item: the editor's own shape, plus its id and page. */
export type IAnnotationItem = MarkupItem & IItemIdentity;
export type INewAnnotationItem = IAnnotationItem;

export interface IAnnotationAuthor {
  userId: string;
  name: string;
}

export interface IDocumentAnnotation {
  id: string;
  documentId: string;
  seq: number;
  note: string;
  items: IAnnotationItem[];
  createdBy: IAnnotationAuthor;
  createdAt: string; // ISO
}

interface IAnnotationsResponse {
  annotations: IDocumentAnnotation[];
}

const base = (siteId: string) => `/api/sites/${encodeURIComponent(siteId)}/documents/annotations`;

/** A refused annotations request, with the status that says why. */
export class AnnotationRequestError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'AnnotationRequestError';
  }
}

async function handle(res: Response): Promise<never> {
  const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
  throw new AnnotationRequestError(res.status, body?.error?.message ?? `annotations request failed (${res.status})`);
}

/** In the order they paint. */
export async function fetchAnnotations(
  siteId: string,
  fileId: string,
  signal?: AbortSignal,
): Promise<IDocumentAnnotation[]> {
  const res = await authedFetch(`${base(siteId)}?fileId=${encodeURIComponent(fileId)}`, { signal });
  if (!res.ok) return handle(res);
  const body = (await res.json()) as IAnnotationsResponse;
  return body.annotations;
}

export async function createAnnotation(
  siteId: string,
  input: { fileId: string; note: string; items: INewAnnotationItem[] },
): Promise<IDocumentAnnotation> {
  const res = await authedFetch(base(siteId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) return handle(res);
  return (await res.json()) as IDocumentAnnotation;
}

/** Soft on the server: it stops being listed, and the record survives. */
export async function deleteAnnotation(siteId: string, annotationId: string): Promise<void> {
  const res = await authedFetch(`${base(siteId)}/${encodeURIComponent(annotationId)}`, { method: 'DELETE' });
  if (!res.ok) await handle(res);
}

/** Ids are minted here: the editor has no use for them until a save. */
export function toWireItems(itemsByPage: ItemsByPage): INewAnnotationItem[] {
  return Object.entries(itemsByPage).flatMap(([page, items]) =>
    (items ?? []).map((item) => ({ ...item, id: crypto.randomUUID(), page: Number(page) })),
  );
}

export function groupByPage<T extends { page: number }>(items: T[]): Record<number, T[]> {
  const byPage: Record<number, T[]> = {};
  for (const item of items) (byPage[item.page] ??= []).push(item);
  return byPage;
}
